import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Alert,
    CircularProgress,
    Chip,
    Avatar,
    Modal,
    ModalDialog,
    DialogTitle,
    DialogContent,
    Button,
    Input,
    List,
    ListItem,
    Sheet,
    Tabs,
    TabList,
    Tab,
    TabPanel,
    tabClasses
} from '@mui/joy';
import PersonIcon from '@mui/icons-material/Person';
import SchoolIcon from '@mui/icons-material/School';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import HomeIcon from '@mui/icons-material/Home';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import axiosConfig from '../util/axiosConfig';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LanguageIcon from '@mui/icons-material/Language';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { useNavigate } from 'react-router-dom';
import Attendance from './Attendance';

const ORANGE = '#eb7a24';
const ORANGE_DARK = '#e55125';

const PAGE_CONTAINER_SX = {
    p: 2,
    width: '100%',
    maxWidth: '1100px',
    mx: 'auto',
    minWidth: 0,
    boxSizing: 'border-box'
};

const Profile = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [profileData, setProfileData] = useState(null);
    const [photoUrl, setPhotoUrl] = useState(null);
    const [canEdit, setCanEdit] = useState(false);
    const [editExpiresAt, setEditExpiresAt] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const navigate = useNavigate();

    // College selection modal state
    const [collegeModalOpen, setCollegeModalOpen] = useState(false);
    const [collegeType, setCollegeType] = useState(null); // 'ug' or 'pg'
    const [collegeSearchTerm, setCollegeSearchTerm] = useState('');
    const [collegeSearchResults, setCollegeSearchResults] = useState([]);
    const [collegeSearchLoading, setCollegeSearchLoading] = useState(false);
    const [collegeUpdateLoading, setCollegeUpdateLoading] = useState(false);
    const [collegeSearchOffset, setCollegeSearchOffset] = useState(0);
    const [collegeHasMore, setCollegeHasMore] = useState(false);
    const [collegeLoadingMore, setCollegeLoadingMore] = useState(false);
    const [selectedCollege, setSelectedCollege] = useState(null); // Store selected college for confirmation
    const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);

    const fetchEditPermission = useCallback(async () => {
        try {
            const resp = await axiosConfig.get('/students/profile/edit-permission');
            setCanEdit(Boolean(resp.data?.canEdit));
            setEditExpiresAt(resp.data?.expiresAt || null);
        } catch (e) {
            // ignore
        }
    }, []);

    const fetchProfile = useCallback(async () => {
        try {
            setError(null);
            const response = await axiosConfig.get('/students/profile');
            setProfileData(response.data);
            setPhotoUrl(response.data?.photoUrl || null);
            fetchEditPermission();
        } catch (err) {
            console.error('Error fetching profile:', err);
            setError(err.response?.data?.errorMessage || 'Failed to load profile data');
        } finally {
            setLoading(false);
        }
    }, [fetchEditPermission]);

    useEffect(() => {
        fetchProfile();
    }, [fetchProfile]);

    // College search with debouncing
    const searchColleges = useCallback(async (searchTerm, offset = 0, append = false) => {
        if (!searchTerm || searchTerm.trim().length === 0) {
            setCollegeSearchResults([]);
            setCollegeHasMore(false);
            setCollegeSearchOffset(0);
            return;
        }

        if (offset === 0) {
            setCollegeSearchLoading(true);
        } else {
            setCollegeLoadingMore(true);
        }

        try {
            const response = await axiosConfig.get('/students/colleges/search', {
                params: { q: searchTerm, offset: offset }
            });

            if (append) {
                setCollegeSearchResults(prev => [...prev, ...(response.data.results || [])]);
            } else {
                setCollegeSearchResults(response.data.results || []);
            }
            setCollegeHasMore(response.data.hasMore || false);
            setCollegeSearchOffset(offset);
        } catch (err) {
            console.error('Error searching colleges:', err);
            if (!append) {
                setCollegeSearchResults([]);
            }
            setCollegeHasMore(false);
        } finally {
            setCollegeSearchLoading(false);
            setCollegeLoadingMore(false);
        }
    }, []);

    // Load more colleges (infinite scroll)
    const loadMoreColleges = useCallback(() => {
        if (!collegeSearchLoading && !collegeLoadingMore && collegeHasMore && collegeSearchTerm) {
            const nextOffset = collegeSearchOffset + 50;
            searchColleges(collegeSearchTerm, nextOffset, true);
        }
    }, [collegeSearchTerm, collegeSearchOffset, collegeHasMore, collegeSearchLoading, collegeLoadingMore, searchColleges]);

    // Debounced search effect
    useEffect(() => {
        const timer = setTimeout(() => {
            if (collegeModalOpen) {
                setCollegeSearchOffset(0);
                searchColleges(collegeSearchTerm, 0, false);
            }
        }, 300); // 300ms debounce

        return () => clearTimeout(timer);
    }, [collegeSearchTerm, collegeModalOpen, searchColleges]);

    // Open college selection modal
    const handleOpenCollegeModal = (type) => {
        setCollegeType(type);
        setCollegeSearchTerm('');
        setCollegeSearchResults([]);
        setCollegeSearchOffset(0);
        setCollegeHasMore(false);
        setCollegeModalOpen(true);
    };

    // Close college selection modal
    const handleCloseCollegeModal = () => {
        setCollegeModalOpen(false);
        setCollegeType(null);
        setCollegeSearchTerm('');
        setCollegeSearchResults([]);
        setCollegeSearchOffset(0);
        setCollegeHasMore(false);
        setSelectedCollege(null);
        setShowConfirmationDialog(false);
    };

    // Handle scroll for infinite loading
    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        // Load more when user scrolls to within 100px of bottom
        if (scrollHeight - scrollTop - clientHeight < 100) {
            loadMoreColleges();
        }
    };

    // Handle college selection - show confirmation dialog
    const handleSelectCollege = (college) => {
        setSelectedCollege(college);
        setShowConfirmationDialog(true);
    };

    // Handle college confirmation and update
    const handleConfirmCollegeUpdate = async () => {
        if (!selectedCollege) return;

        setCollegeUpdateLoading(true);
        try {
            await axiosConfig.put('/students/profile/college-code', {
                collegeCode: selectedCollege.collegecode,
                type: collegeType
            });

            // Refresh profile data
            await fetchProfile();

            // Close both dialogs
            setShowConfirmationDialog(false);
            handleCloseCollegeModal();

            // Show success message (you can use a toast/notification here)
            setError(null);
        } catch (err) {
            console.error('Error updating college:', err);
            setError(err.response?.data?.errorMessage || 'Failed to update college');
        } finally {
            setCollegeUpdateLoading(false);
        }
    };

    // Handle cancel confirmation - go back to college selection
    const handleCancelConfirmation = () => {
        setShowConfirmationDialog(false);
        setSelectedCollege(null);
    };

    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '400px',
                    gap: 2
                }}
            >
                <CircularProgress size="lg" />
                <Typography level="body-md">Loading profile data...</Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert color="danger" variant="soft">
                    {error}
                </Alert>
            </Box>
        );
    }

    if (!profileData) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert color="warning" variant="soft">
                    No profile data available
                </Alert>
            </Box>
        );
    }

    const hasValue = (v) => !(v === undefined || v === null || v === '' || v === '0' || v === 0);

    // Neat label/value rows with dividers; hides empty fields automatically
    const InfoList = ({ fields, emptyMessage = 'No details available' }) => {
        const visible = fields.filter((f) => hasValue(f.value));
        if (visible.length === 0) {
            return (
                <Alert color="neutral" variant="soft" sx={{ my: 1.5 }}>
                    {emptyMessage}
                </Alert>
            );
        }
        return (
            <Box>
                {visible.map((f, i) => (
                    <Box
                        key={f.label}
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: '220px 1fr' },
                            gap: { xs: 0.25, sm: 2 },
                            alignItems: 'baseline',
                            py: 1.25,
                            borderBottom: i < visible.length - 1 ? '1px solid' : 'none',
                            borderColor: 'divider'
                        }}
                    >
                        <Typography level="body-sm" sx={{ color: '#6e7072', fontWeight: 'md' }}>
                            {f.label}
                        </Typography>
                        <Typography level="body-md" sx={{ color: '#404041', wordBreak: 'break-word' }}>
                            {f.value}
                        </Typography>
                    </Box>
                ))}
            </Box>
        );
    };

    // Bordered section with a tinted header bar and optional action button
    const Section = ({ title, icon, action, children }) => (
        <Sheet variant="outlined" sx={{ borderRadius: 'md', overflow: 'hidden', mb: 2, bgcolor: 'background.surface', width: '100%' }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    flexWrap: 'wrap',
                    px: 2.5,
                    py: 1.5,
                    bgcolor: '#fff7f0',
                    borderBottom: '1px solid',
                    borderColor: 'divider'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {icon}
                    <Typography level="title-md" sx={{ color: ORANGE, fontWeight: 'bold' }}>
                        {title}
                    </Typography>
                </Box>
                {action}
            </Box>
            <Box sx={{ px: 2.5, py: 1 }}>{children}</Box>
        </Sheet>
    );

    const UpdateCollegeButton = ({ type }) => (
        <Button
            size="sm"
            variant="outlined"
            startDecorator={<SchoolIcon />}
            onClick={() => handleOpenCollegeModal(type)}
            sx={{
                borderColor: ORANGE,
                color: ORANGE,
                '&:hover': { borderColor: ORANGE_DARK, backgroundColor: '#f19b30', color: 'white' }
            }}
        >
            Update College
        </Button>
    );

    const pct = (v) => (hasValue(v) ? `${v}%` : null);

    const personalFields = [
        { label: 'Name', value: profileData.name },
        { label: 'Gender', value: profileData.gender },
        { label: 'Date of Birth', value: profileData.dateofbirth },
        { label: 'Blood Group', value: profileData.bgroup },
        { label: 'Join Date', value: profileData.joindate }
    ];

    const contactFields = [
        { label: 'Email', value: profileData.email },
        { label: 'Mobile', value: profileData.mobileno },
        { label: 'Landline', value: profileData.landline },
        { label: "Father's Name", value: profileData.fathername },
        { label: "Father's Contact", value: profileData.fathercontact }
    ];

    const addressFields = [
        { label: 'Permanent Address', value: profileData.address },
        { label: 'Local Address', value: profileData.laddress },
        { label: 'City', value: profileData.city },
        { label: 'State', value: profileData.state }
    ];

    const ugFields = [
        { label: 'Qualification', value: !profileData.hasPg ? profileData.qualification : null },
        { label: 'Specialization', value: profileData.ugspec },
        { label: 'Board/University', value: profileData.ugboard },
        { label: 'College', value: profileData.ugcollege },
        { label: 'College Address', value: profileData.ugcolladdr },
        { label: 'City', value: profileData.ugcity },
        { label: 'State', value: profileData.ugstate },
        { label: 'District', value: profileData.ugcollegedistrict },
        { label: 'Year of Passing', value: profileData.ugyop },
        { label: 'Certificate %', value: pct(profileData.ugcertper) },
        { label: 'Aggregate %', value: pct(profileData.ugaggrper) },
        { label: 'History of UG Backlogs', value: profileData.tugbacklog },
        { label: 'Current Backlogs', value: profileData.backlog },
        { label: 'Site', value: profileData.ugsite },
        { label: 'Project', value: profileData.project }
    ];

    const pgFields = [
        { label: 'Qualification', value: profileData.pgqualification },
        { label: 'Specialization', value: profileData.pgspec },
        { label: 'Board/University', value: profileData.pgboard },
        { label: 'College', value: profileData.pgcollege },
        { label: 'College Address', value: profileData.pgcolladdr },
        { label: 'City', value: profileData.pgcity },
        { label: 'State', value: profileData.pgstate },
        { label: 'District', value: profileData.pgcollegedistrict },
        { label: 'Year of Passing', value: profileData.pgyop },
        { label: 'Certificate %', value: pct(profileData.pgcertper) },
        { label: 'Aggregate %', value: pct(profileData.pgaggrper) },
        { label: 'History of PG Backlogs', value: profileData.tpgbacklog },
        { label: 'Site', value: profileData.pgsite },
        { label: 'Project', value: profileData.project }
    ];

    const tenthFields = [
        { label: 'Board', value: profileData.xboard },
        { label: 'Year of Passing', value: profileData.xyop },
        { label: 'Percentage', value: pct(profileData.tenthpercentage) }
    ];

    const twelfthFields = [
        { label: 'Board', value: profileData.xiiboard },
        { label: 'Year of Passing', value: profileData.xiiyop },
        { label: 'Percentage', value: pct(profileData.interpercentage) }
    ];

    const diplomaFields = [
        { label: 'Board', value: profileData.diplomaboard },
        { label: 'Year of Passing', value: profileData.diplomayop },
        { label: 'Percentage', value: profileData.diplomapercentage && profileData.diplomapercentage !== '0.00' ? `${profileData.diplomapercentage}%` : null }
    ];

    const experienceFields = [
        { label: 'Experience (Months)', value: hasValue(profileData.expmonth) ? `${profileData.expmonth} months` : null },
        { label: 'Experience', value: profileData.expe }
    ];

    const hasTenth = tenthFields.some((f) => hasValue(f.value));
    const hasTwelfth = twelfthFields.some((f) => hasValue(f.value));
    const hasDiploma = diplomaFields.some((f) => hasValue(f.value));
    const hasExperience = hasValue(profileData.expe) || hasValue(profileData.expmonth);

    const tabLabels = ['Personal Info', 'Photo', 'Contact Info', 'UG', 'PG', 'Academic', 'Attendance'];

    return (
        <Box sx={PAGE_CONTAINER_SX}>
            {/* Profile header */}
            <Card variant="outlined" sx={{ mb: 2, width: '100%' }}>
                <CardContent
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 2,
                        flexWrap: 'wrap'
                    }}
                >
                    <Avatar
                        src={photoUrl || undefined}
                        alt={profileData.name || 'Student Photo'}
                        sx={{
                            width: 72,
                            height: 72,
                            border: `2px solid ${ORANGE}`,
                            bgcolor: '#f19b30',
                            fontSize: '1.75rem',
                            '& img': { objectFit: 'cover', objectPosition: 'center' }
                        }}
                    >
                        {profileData.name ? profileData.name.charAt(0).toUpperCase() : 'S'}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 200 }}>
                        <Typography level="h4" sx={{ color: '#404041' }}>
                            {profileData.name || 'Student'}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.75 }}>
                            {hasValue(profileData.studentId || profileData.studentid) && (
                                <Chip size="sm" variant="soft" color="neutral">
                                    ID: {profileData.studentId || profileData.studentid}
                                </Chip>
                            )}
                            {hasValue(profileData.gender) && (
                                <Chip size="sm" variant="soft" color="neutral">
                                    {profileData.gender}
                                </Chip>
                            )}
                            {hasValue(profileData.bgroup) && (
                                <Chip size="sm" variant="soft" color="danger">
                                    {profileData.bgroup}
                                </Chip>
                            )}
                        </Box>
                    </Box>
                    {canEdit && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', sm: 'flex-end' }, gap: 0.5 }}>
                            <Button
                                onClick={() => navigate('/update-profile')}
                                startDecorator={<EditIcon />}
                                sx={{ backgroundColor: ORANGE, '&:hover': { backgroundColor: ORANGE_DARK } }}
                            >
                                Edit Profile
                            </Button>
                            {editExpiresAt && (
                                <Typography level="body-xs" sx={{ color: '#6e7072' }}>
                                    Editing enabled until {new Date(editExpiresAt).toLocaleString()}
                                </Typography>
                            )}
                        </Box>
                    )}
                </CardContent>
            </Card>

            <Tabs value={activeTab} onChange={(event, value) => setActiveTab(value)} sx={{ bgcolor: 'transparent', width: '100%' }}>
                <TabList
                    disableUnderline
                    sx={{
                        p: 0.5,
                        gap: 0.5,
                        borderRadius: 'lg',
                        bgcolor: 'neutral.100',
                        overflowX: 'auto',
                        scrollSnapType: 'x mandatory',
                        '&::-webkit-scrollbar': { display: 'none' },
                        [`& .${tabClasses.root}`]: {
                            flex: 'none',
                            scrollSnapAlign: 'start',
                            whiteSpace: 'nowrap',
                            borderRadius: 'md',
                            fontWeight: 'md',
                            color: '#606162',
                            [`&.${tabClasses.selected}`]: {
                                color: ORANGE,
                                fontWeight: 'lg',
                                bgcolor: 'background.surface',
                                boxShadow: 'sm'
                            }
                        }
                    }}
                >
                    {tabLabels.map((label) => (
                        <Tab key={label} disableIndicator>
                            {label}
                        </Tab>
                    ))}
                </TabList>

                {/* Personal Info */}
                <TabPanel value={0} sx={{ px: 0, py: 2, width: '100%' }}>
                    <Section title="Personal Information" icon={<PersonIcon sx={{ color: ORANGE }} />}>
                        <InfoList fields={personalFields} emptyMessage="No personal details available" />
                    </Section>
                </TabPanel>

                {/* Photo */}
                <TabPanel value={1} sx={{ px: 0, py: 2, width: '100%' }}>
                    <Section title="Student Photo" icon={<PhotoCameraIcon sx={{ color: ORANGE }} />}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
                            <Avatar
                                src={photoUrl || undefined}
                                alt={profileData.name || 'Student Photo'}
                                sx={{
                                    // Passport-like frame (3.5 x 4.5cm ≈ 0.78 aspect ratio)
                                    width: 180,
                                    height: 225,
                                    maxWidth: '100%',
                                    borderRadius: 10,
                                    border: `3px solid ${ORANGE}`,
                                    mb: 1.5,
                                    bgcolor: '#f19b30',
                                    fontSize: '3rem',
                                    '& img': { objectFit: 'cover', objectPosition: 'center' }
                                }}
                            >
                                {profileData.name ? profileData.name.charAt(0).toUpperCase() : 'S'}
                            </Avatar>
                            <Typography level="title-sm" sx={{ color: '#404041' }}>
                                {profileData.name}
                            </Typography>
                            <Typography level="body-sm" sx={{ color: '#6e7072' }}>
                                Student Photo
                            </Typography>
                        </Box>
                    </Section>
                </TabPanel>

                {/* Contact Info */}
                <TabPanel value={2} sx={{ px: 0, py: 2, width: '100%' }}>
                    <Section title="Contact Information" icon={<ContactPhoneIcon sx={{ color: ORANGE }} />}>
                        <InfoList fields={contactFields} emptyMessage="No contact details available" />
                    </Section>
                    <Section title="Address Information" icon={<HomeIcon sx={{ color: ORANGE }} />}>
                        <InfoList fields={addressFields} emptyMessage="No address details available" />
                    </Section>
                </TabPanel>

                {/* UG */}
                <TabPanel value={3} sx={{ px: 0, py: 2, width: '100%' }}>
                    <Section
                        title="Under Graduation (UG)"
                        icon={<SchoolIcon sx={{ color: ORANGE }} />}
                        action={!profileData.ugcollegecode ? <UpdateCollegeButton type="ug" /> : null}
                    >
                        <InfoList fields={ugFields} emptyMessage="No UG details available" />
                    </Section>
                    {!profileData.hasPg && hasExperience && (
                        <Section title="Experience" icon={<WorkspacePremiumIcon sx={{ color: ORANGE }} />}>
                            <InfoList fields={experienceFields} />
                        </Section>
                    )}
                </TabPanel>

                {/* PG */}
                <TabPanel value={4} sx={{ px: 0, py: 2, width: '100%' }}>
                    <Section
                        title="Post Graduation (PG)"
                        icon={<SchoolIcon sx={{ color: ORANGE }} />}
                        action={profileData.hasPg && !profileData.pgcollegecode ? <UpdateCollegeButton type="pg" /> : null}
                    >
                        {profileData.hasPg
                            ? <InfoList fields={pgFields} emptyMessage="No PG details available" />
                            : <Alert color="neutral" variant="soft">This student has no Post Graduation (PG) data.</Alert>
                        }
                    </Section>
                    {profileData.hasPg && hasExperience && (
                        <Section title="Experience" icon={<WorkspacePremiumIcon sx={{ color: ORANGE }} />}>
                            <InfoList fields={experienceFields} />
                        </Section>
                    )}
                </TabPanel>

                {/* Academic (10th / 12th / Diploma) */}
                <TabPanel value={5} sx={{ px: 0, py: 2, width: '100%' }}>
                    {!hasTenth && !hasTwelfth && !hasDiploma && (
                        <Alert color="neutral" variant="soft">
                            No academic details available
                        </Alert>
                    )}
                    {hasTenth && (
                        <Section title="10th Class" icon={<SchoolIcon sx={{ color: ORANGE }} />}>
                            <InfoList fields={tenthFields} />
                        </Section>
                    )}
                    {hasTwelfth && (
                        <Section title="12th Class / Intermediate" icon={<SchoolIcon sx={{ color: ORANGE }} />}>
                            <InfoList fields={twelfthFields} />
                        </Section>
                    )}
                    {hasDiploma && (
                        <Section title="Diploma" icon={<SchoolIcon sx={{ color: ORANGE }} />}>
                            <InfoList fields={diplomaFields} />
                        </Section>
                    )}
                </TabPanel>

                {/* Attendance */}
                <TabPanel value={6} sx={{ px: 0, py: 2, width: '100%' }}>
                    <Attendance />
                </TabPanel>

            </Tabs>

            {/* College Selection Modal */}
            <Modal open={collegeModalOpen} onClose={handleCloseCollegeModal}>
                <ModalDialog
                    sx={{
                        width: '100%',
                        height: '100%',
                        maxWidth: '100%',
                        maxHeight: '100%',
                        m: 0,
                        borderRadius: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        p: 0
                    }}
                >
                    <DialogTitle sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                        Update {collegeType === 'ug' ? 'UG' : 'PG'} College
                    </DialogTitle>
                    <DialogContent sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
                            <Input
                                placeholder="Search by college name, address, university, or website..."
                                value={collegeSearchTerm}
                                onChange={(e) => setCollegeSearchTerm(e.target.value)}
                                startDecorator={<SearchIcon />}
                                sx={{
                                    '&:hover': { borderColor: '#ea7a23' },
                                    '&:focus-within': { borderColor: '#ea7a23' }
                                }}
                            />

                            <Box
                                sx={{
                                    flex: 1,
                                    overflowY: 'auto',
                                    overflowX: 'hidden',
                                    minHeight: 0
                                }}
                                onScroll={handleScroll}
                            >
                                {collegeSearchLoading && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                        <CircularProgress size="md" />
                                    </Box>
                                )}

                                {!collegeSearchLoading && collegeSearchTerm && collegeSearchResults.length === 0 && (
                                    <Alert color="neutral" variant="soft" sx={{ m: 2 }}>
                                        No colleges found. Try a different search term.
                                    </Alert>
                                )}

                                {!collegeSearchLoading && collegeSearchTerm && collegeSearchResults.length > 0 && (
                                    <List sx={{ p: 0 }}>
                                        {collegeSearchResults.map((college, index) => (
                                            <ListItem
                                                key={college.collegecode || index}
                                                sx={{
                                                    p: 0,
                                                    mb: 1,
                                                    '&:last-child': { mb: 0 }
                                                }}
                                            >
                                                <Card
                                                    variant="outlined"
                                                    sx={{
                                                        width: '100%',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s',
                                                        '&:hover': {
                                                            borderColor: '#eb7a24',
                                                            boxShadow: '0 2px 8px rgba(235, 122, 36, 0.2)',
                                                            transform: 'translateY(-2px)'
                                                        }
                                                    }}
                                                    onClick={() => handleSelectCollege(college)}
                                                >
                                                    <CardContent sx={{ p: 2 }}>
                                                        <Typography
                                                            level="title-lg"
                                                            sx={{
                                                                fontWeight: 'bold',
                                                                color: '#eb7a24',
                                                                mb: 1.5
                                                            }}
                                                        >
                                                            {college.collegename || 'N/A'}
                                                        </Typography>

                                                        {college.universityname && (
                                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                                                                <SchoolIcon sx={{ fontSize: '18px', color: '#6e7072', mt: 0.25, flexShrink: 0 }} />
                                                                <Typography level="body-sm" sx={{ color: '#606162', flex: 1 }}>
                                                                    {college.universityname}
                                                                </Typography>
                                                            </Box>
                                                        )}

                                                        {college.collegeaddress && (
                                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                                                                <LocationOnIcon sx={{ fontSize: '18px', color: '#e55125', mt: 0.25, flexShrink: 0 }} />
                                                                <Typography level="body-sm" sx={{ color: '#606162', flex: 1 }}>
                                                                    {college.collegeaddress}
                                                                </Typography>
                                                            </Box>
                                                        )}

                                                        {college.website && (
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <LanguageIcon sx={{ fontSize: '18px', color: '#2196f3', flexShrink: 0 }} />
                                                                <Typography
                                                                    level="body-sm"
                                                                    sx={{
                                                                        color: '#606162',
                                                                        textDecoration: 'none',
                                                                        '&:hover': {
                                                                            textDecoration: 'underline',
                                                                            color: '#2196f3'
                                                                        }
                                                                    }}
                                                                    component="a"
                                                                    href={college.website.startsWith('http') ? college.website : `http://${college.website}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    onClick={(e) => e.stopPropagation()}
                                                                >
                                                                    {college.website}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                    </CardContent>
                                                </Card>
                                            </ListItem>
                                        ))}

                                        {collegeLoadingMore && (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                                <CircularProgress size="sm" />
                                            </Box>
                                        )}

                                        {!collegeHasMore && collegeSearchResults.length > 0 && (
                                            <Box sx={{ textAlign: 'center', p: 2 }}>
                                                <Typography level="body-sm" sx={{ color: '#6e7072' }}>
                                                    No more results
                                                </Typography>
                                            </Box>
                                        )}
                                    </List>
                                )}

                                {!collegeSearchTerm && (
                                    <Alert color="info" variant="soft" sx={{ m: 2 }}>
                                        Start typing to search for colleges...
                                    </Alert>
                                )}
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                                <Button
                                    variant="outlined"
                                    color="neutral"
                                    onClick={handleCloseCollegeModal}
                                    disabled={collegeUpdateLoading}
                                >
                                    Cancel
                                </Button>
                            </Box>
                        </Box>
                    </DialogContent>
                </ModalDialog>
            </Modal>

            {/* College Confirmation Dialog */}
            <Modal open={showConfirmationDialog} onClose={handleCancelConfirmation}>
                <ModalDialog
                    sx={{
                        maxWidth: 600,
                        width: '90%'
                    }}
                >
                    <DialogTitle>
                        Confirm College Selection
                    </DialogTitle>
                    <DialogContent>
                        {selectedCollege && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Alert color="info" variant="soft" sx={{ mb: 2 }}>
                                    Please review the college details before confirming.
                                </Alert>

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Box>
                                        <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium', mb: 0.5 }}>
                                            College Name
                                        </Typography>
                                        <Typography level="body-lg" sx={{ fontWeight: 'bold', color: '#eb7a24' }}>
                                            {selectedCollege.collegename || 'N/A'}
                                        </Typography>
                                    </Box>

                                    {selectedCollege.universityname && (
                                        <Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                <SchoolIcon sx={{ fontSize: '18px', color: '#6e7072' }} />
                                                <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium' }}>
                                                    University
                                                </Typography>
                                            </Box>
                                            <Typography level="body-lg" sx={{ color: '#404041', ml: 4 }}>
                                                {selectedCollege.universityname}
                                            </Typography>
                                        </Box>
                                    )}

                                    {selectedCollege.collegeaddress && (
                                        <Box>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
                                                <LocationOnIcon sx={{ fontSize: '18px', color: '#e55125', mt: 0.25 }} />
                                                <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium' }}>
                                                    Address
                                                </Typography>
                                            </Box>
                                            <Typography level="body-lg" sx={{ color: '#404041', ml: 4 }}>
                                                {selectedCollege.collegeaddress}
                                            </Typography>
                                        </Box>
                                    )}

                                    {selectedCollege.website && (
                                        <Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                <LanguageIcon sx={{ fontSize: '18px', color: '#2196f3' }} />
                                                <Typography level="body-sm" sx={{ color: '#606162', fontWeight: 'medium' }}>
                                                    Website
                                                </Typography>
                                            </Box>
                                            <Typography
                                                level="body-lg"
                                                sx={{
                                                    color: '#2196f3',
                                                    ml: 4,
                                                    textDecoration: 'none',
                                                    '&:hover': {
                                                        textDecoration: 'underline'
                                                    }
                                                }}
                                                component="a"
                                                href={selectedCollege.website.startsWith('http') ? selectedCollege.website : `http://${selectedCollege.website}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {selectedCollege.website}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        )}
                    </DialogContent>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                        <Button
                            variant="outlined"
                            color="neutral"
                            onClick={handleCancelConfirmation}
                            disabled={collegeUpdateLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="solid"
                            color="primary"
                            onClick={handleConfirmCollegeUpdate}
                            disabled={collegeUpdateLoading}
                            loading={collegeUpdateLoading}
                            sx={{
                                backgroundColor: '#eb7a24',
                                '&:hover': {
                                    backgroundColor: '#e55125'
                                }
                            }}
                        >
                            OK
                        </Button>
                    </Box>
                </ModalDialog>
            </Modal>
        </Box>
    );
};

export default Profile;
